import { cs } from '../lib/currency';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Users,
  Plus,
  GraduationCap,
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  UserPlus,
  Trash2,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const MyFamily = () => {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [attendance, setAttendance] = useState({ attendance: [], summary: {} });
  const [billing, setBilling] = useState({ total_monthly: 0, enrollments: [], credit_balance: 0 });
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('children');
  
  // Dialogs
  const [addChildDialog, setAddChildDialog] = useState(false);
  const [linkPlayerDialog, setLinkPlayerDialog] = useState(false);
  const [enrollDialog, setEnrollDialog] = useState(false);
  const [_selectedChild, _setSelectedChild] = useState(null);
  
  // Available players for linking
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [playerSearchTerm, setPlayerSearchTerm] = useState('');
  
  // Form states
  const [childForm, setChildForm] = useState({
    full_name: '',
    date_of_birth: '',
    gender: '',
    skill_level: 'beginner',
    medical_notes: ''
  });
  
  const [enrollForm, setEnrollForm] = useState({
    player_id: '',
    program_id: '',
    class_id: '',
    payment_frequency: 'monthly',
    notes: ''
  });
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [childrenRes, enrollmentsRes, attendanceRes, billingRes, programsRes] = await Promise.all([
        api.get('/my-family/children'),
        api.get('/my-family/enrollments'),
        api.get('/my-family/attendance'),
        api.get('/my-family/billing'),
        api.get('/my-family/programs')
      ]);
      
      setChildren(childrenRes.data);
      setEnrollments(enrollmentsRes.data);
      setAttendance(attendanceRes.data);
      setBilling(billingRes.data);
      setPrograms(programsRes.data);
    } catch (error) {
      toast.error('Failed to load family data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddChild = async () => {
    if (!childForm.full_name || !childForm.date_of_birth) {
      toast.error('Name and date of birth are required');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/my-family/children', childForm);
      toast.success('Child added successfully');
      setAddChildDialog(false);
      setChildForm({
        full_name: '',
        date_of_birth: '',
        gender: '',
        skill_level: 'beginner',
        medical_notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add child');
    } finally {
      setSaving(false);
    }
  };

  const handleEnroll = async () => {
    if (!enrollForm.player_id || !enrollForm.program_id) {
      toast.error('Please select a child and program');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.post('/my-family/enroll', enrollForm);
      
      if (response.data.auto_approved) {
        toast.success('Enrollment approved automatically!');
      } else {
        toast.success('Enrollment request submitted. Awaiting admin approval.');
      }
      
      setEnrollDialog(false);
      setEnrollForm({
        player_id: '',
        program_id: '',
        class_id: '',
        payment_frequency: 'monthly',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit enrollment');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenLinkPlayer = async () => {
    try {
      // Fetch all players that are not already in my family
      const response = await api.get('/players');
      const myChildIds = children.map(c => c.id);
      const available = response.data.filter(p => !myChildIds.includes(p.id));
      setAvailablePlayers(available);
      setSelectedPlayerId('');
      setPlayerSearchTerm('');
      setLinkPlayerDialog(true);
    } catch (error) {
      toast.error('Failed to load players');
    }
  };

  const handleLinkPlayer = async () => {
    if (!selectedPlayerId) {
      toast.error('Please select a player to link');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/my-family/link-player/${selectedPlayerId}`);
      toast.success('Player linked to your family');
      setLinkPlayerDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link player');
    } finally {
      setSaving(false);
    }
  };

  const filteredAvailablePlayers = availablePlayers.filter(p => 
    p.full_name?.toLowerCase().includes(playerSearchTerm.toLowerCase()) ||
    p.numeric_id?.toLowerCase().includes(playerSearchTerm.toLowerCase())
  );

  const handleRemoveChild = async (childId, childName) => {
    if (!window.confirm(`Are you sure you want to remove ${childName}?`)) {
      return;
    }
    
    try {
      await api.delete(`/my-family/children/${childId}`);
      toast.success('Child removed successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove child');
    }
  };

  const selectedProgram = programs.find(p => p.id === enrollForm.program_id);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="my-family-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Family</h1>
            <p className="text-slate-500">Manage your children's enrollments and activities</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setAddChildDialog(true)} data-testid="add-child-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Child
            </Button>
            <Button onClick={handleOpenLinkPlayer} variant="outline" data-testid="link-player-btn">
              <Plus className="h-4 w-4 mr-2" />
              Link Existing Player
            </Button>
            {children.length > 0 && (
              <Button onClick={() => setEnrollDialog(true)} variant="outline" data-testid="enroll-child-btn">
                <GraduationCap className="h-4 w-4 mr-2" />
                Enroll in Program
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Children</p>
                  <p className="text-2xl font-bold">{children.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <GraduationCap className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Active Enrollments</p>
                  <p className="text-2xl font-bold">
                    {enrollments.filter(e => ['current', 'active', 'approved'].includes(e.status)).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Attendance Rate</p>
                  <p className="text-2xl font-bold">{attendance.summary.attendance_rate || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Monthly Total</p>
                  <p className="text-2xl font-bold">{cs()}{billing.total_monthly.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="children">Children</TabsTrigger>
            <TabsTrigger value="enrollments">Enrollments</TabsTrigger>
            <TabsTrigger value="attendance">Attendance</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
          </TabsList>

          {/* Children Tab */}
          <TabsContent value="children" className="mt-4">
            {children.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Children Added</h3>
                  <p className="text-slate-500 mb-4">Add your children to enroll them in programs</p>
                  <Button onClick={() => setAddChildDialog(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Your First Child
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {children.map(child => (
                  <Card key={child.id} data-testid={`child-card-${child.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-bold text-lg">
                              {child.full_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <CardTitle className="text-lg">{child.full_name}</CardTitle>
                            <CardDescription>
                              {child.age ? `${child.age} years old` : 'Age not set'}
                            </CardDescription>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-500 hover:bg-red-50"
                          onClick={() => handleRemoveChild(child.id, child.full_name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">DOB:</span>
                          <span>{child.date_of_birth ? new Date(child.date_of_birth + 'T00:00:00').toLocaleDateString() : '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Skill Level:</span>
                          <Badge variant="outline">{child.skill_level || 'Beginner'}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Enrollments:</span>
                          <Badge className="bg-blue-100 text-blue-700">{child.enrollment_count}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Enrollments Tab */}
          <TabsContent value="enrollments" className="mt-4">
            {enrollments.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <GraduationCap className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Enrollments</h3>
                  <p className="text-slate-500 mb-4">Enroll your children in cricket programs</p>
                  {children.length > 0 && (
                    <Button onClick={() => setEnrollDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Enroll in Program
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {enrollments.map(enrollment => (
                  <Card key={enrollment.id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{enrollment.player_name}</p>
                          <p className="text-sm text-slate-500">
                            {enrollment.program_name} - {enrollment.class_name || 'No class assigned'}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-medium">{cs()}{enrollment.monthly_fee?.toFixed(2)}/mo</p>
                            <p className="text-xs text-slate-500">{enrollment.payment_frequency}</p>
                          </div>
                          <Badge className={
                            enrollment.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            enrollment.status === 'approved' || enrollment.status === 'current' || enrollment.status === 'active' 
                              ? 'bg-green-100 text-green-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {enrollment.status}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Recent Attendance</CardTitle>
                <CardDescription>
                  {attendance.summary.present || 0} present, {attendance.summary.absent || 0} absent out of {attendance.summary.total || 0} sessions
                </CardDescription>
              </CardHeader>
              <CardContent>
                {attendance.attendance.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No attendance records yet</p>
                ) : (
                  <div className="space-y-2">
                    {attendance.attendance.slice(0, 20).map((record, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div>
                          <p className="font-medium">{record.player_name}</p>
                          <p className="text-sm text-slate-500">{new Date(record.date).toLocaleDateString()}</p>
                        </div>
                        <Badge className={record.status === 'present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {record.status === 'present' ? <CheckCircle className="h-3 w-3 mr-1" /> : <AlertCircle className="h-3 w-3 mr-1" />}
                          {record.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Monthly Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  {billing.enrollments.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No active enrollments</p>
                  ) : (
                    <div className="space-y-3">
                      {billing.enrollments.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                          <div>
                            <p className="font-medium">{item.player_name}</p>
                            <p className="text-sm text-slate-500">{item.program_name}</p>
                          </div>
                          <p className="font-medium">{cs()}{item.monthly_fee?.toFixed(2)}</p>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-2 font-bold">
                        <p>Total Monthly</p>
                        <p className="text-lg">{cs()}{billing.total_monthly.toFixed(2)}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Credit Balance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    <p className="text-3xl font-bold text-green-600">{cs()}{billing.credit_balance.toFixed(2)}</p>
                    <p className="text-sm text-slate-500 mt-2">Available credit across all enrollments</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Add Child Dialog */}
        <Dialog open={addChildDialog} onOpenChange={setAddChildDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Child</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={childForm.full_name}
                  onChange={(e) => setChildForm({...childForm, full_name: e.target.value})}
                  placeholder="Enter child's full name"
                  data-testid="child-name-input"
                />
              </div>
              <div>
                <Label>Date of Birth *</Label>
                <Input
                  type="date"
                  value={childForm.date_of_birth}
                  onChange={(e) => setChildForm({...childForm, date_of_birth: e.target.value})}
                  data-testid="child-dob-input"
                />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={childForm.gender} onValueChange={(v) => setChildForm({...childForm, gender: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Skill Level</Label>
                <Select value={childForm.skill_level} onValueChange={(v) => setChildForm({...childForm, skill_level: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select skill level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Medical Notes</Label>
                <Textarea
                  value={childForm.medical_notes}
                  onChange={(e) => setChildForm({...childForm, medical_notes: e.target.value})}
                  placeholder="Any allergies, conditions, or notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddChildDialog(false)}>Cancel</Button>
              <Button onClick={handleAddChild} disabled={saving} data-testid="save-child-btn">
                {saving ? 'Adding...' : 'Add Child'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Enroll Dialog */}
        <Dialog open={enrollDialog} onOpenChange={setEnrollDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Enroll in Program</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Select Child *</Label>
                <Select value={enrollForm.player_id} onValueChange={(v) => setEnrollForm({...enrollForm, player_id: v})}>
                  <SelectTrigger data-testid="enroll-child-select">
                    <SelectValue placeholder="Select a child" />
                  </SelectTrigger>
                  <SelectContent>
                    {children.map(child => (
                      <SelectItem key={child.id} value={child.id}>{child.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Select Program *</Label>
                <Select 
                  value={enrollForm.program_id} 
                  onValueChange={(v) => setEnrollForm({...enrollForm, program_id: v, class_id: ''})}
                >
                  <SelectTrigger data-testid="enroll-program-select">
                    <SelectValue placeholder="Select a program" />
                  </SelectTrigger>
                  <SelectContent>
                    {programs.map(program => (
                      <SelectItem key={program.id} value={program.id}>
                        {program.name} - {cs()}{program.price_monthly}/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedProgram?.classes?.length > 0 && (
                <div>
                  <Label>Select Class (Optional)</Label>
                  <Select value={enrollForm.class_id} onValueChange={(v) => setEnrollForm({...enrollForm, class_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a class" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedProgram.classes.map(cls => (
                        <SelectItem key={cls.id} value={cls.id}>
                          {cls.name} {cls.schedule ? `- ${cls.schedule}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label>Payment Frequency</Label>
                <Select value={enrollForm.payment_frequency} onValueChange={(v) => setEnrollForm({...enrollForm, payment_frequency: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="biannual">Bi-Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={enrollForm.notes}
                  onChange={(e) => setEnrollForm({...enrollForm, notes: e.target.value})}
                  placeholder="Any additional notes..."
                />
              </div>
              
              {user?.role !== 'admin' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-700">
                      As a {user?.role}, your enrollment request will require admin approval.
                    </p>
                  </div>
                </div>
              )}
              
              {user?.role === 'admin' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <p className="text-sm text-green-700">
                      As an admin, your enrollment will be auto-approved.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEnrollDialog(false)}>Cancel</Button>
              <Button onClick={handleEnroll} disabled={saving} data-testid="submit-enrollment-btn">
                {saving ? 'Submitting...' : user?.role === 'admin' ? 'Enroll Now' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Existing Player Dialog */}
        <Dialog open={linkPlayerDialog} onOpenChange={setLinkPlayerDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Link Existing Player</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Search Player</Label>
                <Input
                  placeholder="Search by name or ID..."
                  value={playerSearchTerm}
                  onChange={(e) => setPlayerSearchTerm(e.target.value)}
                  data-testid="player-search-input"
                />
              </div>
              
              <div>
                <Label>Select Player *</Label>
                <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                  <SelectTrigger data-testid="link-player-select">
                    <SelectValue placeholder="Select a player to link" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredAvailablePlayers.length === 0 ? (
                      <SelectItem value="none" disabled>No players found</SelectItem>
                    ) : (
                      filteredAvailablePlayers.slice(0, 50).map(player => (
                        <SelectItem key={player.id} value={player.id}>
                          {player.full_name} {player.numeric_id && `(${player.numeric_id})`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {filteredAvailablePlayers.length > 50 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Showing first 50 results. Use search to narrow down.
                  </p>
                )}
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  Linking a player will add them to your "My Family" view without changing their existing parent assignment.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkPlayerDialog(false)}>Cancel</Button>
              <Button onClick={handleLinkPlayer} disabled={saving || !selectedPlayerId} data-testid="confirm-link-btn">
                {saving ? 'Linking...' : 'Link Player'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MyFamily;
